/**
 * Phase 19 — a surface's optimizer rules (hours, texts or gateways): what a
 * rule judges, the honesty line (a rule never acts on missing data), one
 * card per rule and "+ Add a rule". No rule = the surface behaves exactly as
 * before. Rules are saved with their section and inherited with it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { Plus, Scale } from 'lucide-react';
import { DROP_GEN_RULE_LIMITS, type DropGenRule, type FlowOptimizerMetricMeta } from '@trafficpulse/shared';

import { FormulaMetricCatalogProvider } from '@/features/flow-optimizer/tabs/calculation/metric-catalog/FormulaMetricCatalogContext';

import { HOUR_METRIC_CATALOG } from '../general/hour/formula/hour-metric-catalog';
import { addRule, patchRule, removeRule } from './rule-model';
import { RULE_SURFACES, RULES_HONESTY, type RuleSurface } from './rule-surfaces';
import { type FirstRange, RuleCard } from './card/RuleCard';

interface Props<A extends string> {
  surface: RuleSurface;
  rules: DropGenRule<A>[] | undefined;
  onChange: (next: DropGenRule<A>[]) => void;
  /** Inside a labelled row: the cards and "+ Add a rule" only. */
  bare?: boolean;
  /** The formula's first range — what a rule over "the formula's first range" reads. */
  firstRange?: FirstRange;
  /** ruleId → what the rule does to today's result, when it is worth a line ("it changes nothing today"). */
  notes?: Record<string, string>;
  /** The conditions' metrics; default the Flow catalog (a route test on its drops adds SMSWiser's). */
  metrics?: FlowOptimizerMetricMeta[];
}

export function RulesCard<A extends string>({ surface, rules = [], onChange, bare = false, firstRange, notes, metrics = HOUR_METRIC_CATALOG }: Props<A>) {
  const full = rules.length >= DROP_GEN_RULE_LIMITS.rules;
  return (
    <section className="flex flex-col gap-2" data-testid={`rules-${surface}`}>
      {bare ? null : (
        <header className="flex flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <Scale className="h-4 w-4 text-primary" aria-hidden />
            <h4 className="text-sm font-semibold">Rules</h4>
            <span className="text-xs text-muted-foreground">— optional: what the numbers may change</span>
          </div>
          <p className="text-xs text-muted-foreground">{RULE_SURFACES[surface].intro}</p>
          <p className="text-xs text-muted-foreground">{RULES_HONESTY}</p>
        </header>
      )}
      {/* The Flow Optimizer's condition rows read the formulas' metric catalog (no metadata fetch). */}
      <FormulaMetricCatalogProvider metrics={metrics}>
        {rules.map((rule, i) => (
          <RuleCard
            key={rule.id}
            rule={rule}
            surface={surface}
            index={i + 1}
            firstRange={firstRange}
            note={notes?.[rule.id]}
            onChange={(patch) => onChange(patchRule(rules, rule.id, patch as Partial<DropGenRule<A>>))}
            onRemove={() => onChange(removeRule(rules, rule.id))}
          />
        ))}
      </FormulaMetricCatalogProvider>
      <button
        type="button"
        disabled={full}
        onClick={() => onChange(addRule(rules, surface))}
        className="flex w-fit items-center gap-1 rounded-md border border-dashed border-border px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:text-muted-foreground"
        title={full ? `At most ${DROP_GEN_RULE_LIMITS.rules} rules` : undefined}
      >
        <Plus className="h-3 w-3" /> Add a rule
      </button>
    </section>
  );
}
